import { rasterSrc } from "@/generated/raster-images";
import { createFileRoute } from "@tanstack/react-router";
import { PageHero, Section, SectionHeader, CTABand } from "@/components/site-blocks";
import { COVERAGE_AREAS } from "@/lib/site";
import { EnquiryForm } from "@/components/enquiry-form";
import { Clock, MapPin, Star } from "lucide-react";
import { pageSeo } from "@/lib/seo";

export const Route = createFileRoute("/coverage")({
  head: () => ({
    ...pageSeo({
      path: "/coverage",
      title: "Coverage Across Wales & South West England | CRS Energy",
      description:
        "CRS field teams cover Bridgend, Cardiff, Swansea, Newport, Bristol, Bath, Plymouth and across Wales and the South West. Check if we cover your area.",
    }),
  }),
  component: CoveragePage,
});

function CoveragePage() {
  return (
    <>
      <PageHero
        eyebrow="Coverage"
        title="Where we work"
        description="Headquartered in Bridgend with field teams covering all of Wales and the South West of England."
        image={rasterSrc("stock/photo-1473773508845-188df298d2d1")}
        imageAlt="UK landscape"
      />
      <Section>
        <div className="grid lg:grid-cols-[1.2fr_1fr] gap-10 items-start">
          <div>
            <SectionHeader
              eyebrow="Areas we cover"
              title="Wales & the South West of England"
              description="Headquartered in Bridgend, our field teams operate across these areas. Don't see your town? Get in touch and we'll confirm coverage."
            />
            <div className="flex flex-wrap gap-2.5">
              {COVERAGE_AREAS.map((c) => (
                <span
                  key={c}
                  className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-full bg-primary-soft text-primary font-medium border border-primary/10"
                >
                  <MapPin className="h-3.5 w-3.5 text-accent" aria-hidden />
                  {c}
                </span>
              ))}
            </div>
          </div>
          <div>
            <SectionHeader
              title="Not sure if we cover your area?"
              description="Get in touch and the team will confirm coverage and arrange a free survey."
            />
            <EnquiryForm
              formType="contact"
              variant="contact"
              sourcePage="/coverage"
              title="Get in touch"
              submitLabel="Send enquiry"
            />
          </div>
        </div>
      </Section>
      <Section className="bg-card">
        <div className="grid md:grid-cols-3 gap-6 text-center">
          {[
            { icon: MapPin, title: "Local experts", body: "Across Wales and the South West, with deep knowledge of regional housing stock." },
            { icon: Clock, title: "Lines open 24/7", body: "Reactive and emergency response available for insurers and managed estates." },
            { icon: Star, title: "Outstanding service record", body: "Five-star feedback from private and B2B clients alike." },
          ].map((c) => {
            const Icon = c.icon;
            return (
              <div key={c.title} className="rounded-2xl border bg-background p-7">
                <div className="rounded-xl bg-accent/10 p-3 w-fit mx-auto text-accent">
                  <Icon className="h-6 w-6" aria-hidden />
                </div>
                <h3 className="mt-4 text-lg font-semibold">{c.title}</h3>
                <p className="mt-2 text-muted-foreground text-sm">{c.body}</p>
              </div>
            );
          })}
        </div>
      </Section>
      <CTABand />
    </>
  );
}
